import { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, Save, X, ArrowLeft, Upload, Undo, Redo, Download } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { EditCostBudgetForm } from './edit-cost-budget-form';
import type { BudgetData, CostBudget, CostCategory } from '@/types/budget.types';
import { toast } from 'sonner';
import { fetchDefaultCategoriesAsCostCategories } from '@/lib/data-service';

interface EditCostBudgetPageProps {
  budgetData: BudgetData;
  projectStartDate?: string;
  projectEndDate?: string;
  onSave: (updatedBudget: Partial<BudgetData>) => Promise<void>;
  onCancel: () => void;
}


export function EditCostBudgetPage({
  budgetData,
  projectStartDate,
  projectEndDate,
  onSave,
  onCancel,
}: EditCostBudgetPageProps) {
  const currentYear = new Date().getFullYear();
  
  // Calculate years based on project dates
  const years = (() => {
    if (!projectStartDate || !projectEndDate) {
      // Fallback to default range if no project dates
      return Array.from({ length: 11 }, (_, i) => currentYear - 5 + i);
    }
    
    const startYear = new Date(projectStartDate).getFullYear();
    const endYear = new Date(projectEndDate).getFullYear();
    
    // Generate array of years from start to end
    const yearRange: number[] = [];
    for (let year = startYear; year <= endYear; year++) {
      yearRange.push(year);
    }
    
    return yearRange;
  })();
  
  // Set initial selected year to the first year in the range
  const [selectedYear, setSelectedYear] = useState<string>(years[0]?.toString() || currentYear.toString());
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [costBudgetChanges, setCostBudgetChanges] = useState<CostBudget | null>(null);
  const [currentBudgetData, setCurrentBudgetData] = useState<BudgetData>(budgetData);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] = useState(false);

  // Undo/Redo state
  const MAX_HISTORY = 30;
  const [history, setHistory] = useState<CostBudget[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [undoRedoVersion, setUndoRedoVersion] = useState(0);
  const isUndoRedoInProgress = useRef(false);

  // Handle year change
  const handleYearChange = (year: string) => {
    setSelectedYear(year);
    
    const yearNum = parseInt(year);
    const currentYearNum = new Date().getFullYear();
    
    // Create new budget data based on selected year
    const updatedBudgetData: BudgetData = {
      ...budgetData,
      costBudget: {
        ...budgetData.costBudget,
        categories: budgetData.costBudget.categories.map(cat => {
          // If selecting current year, use the original data
          if (yearNum === currentYearNum) {
            return cat;
          }
          
          // If selecting a different year, show empty data
          return {
            ...cat,
            budget: 0,
            actual: 0,
            committed: 0,
            available: 0,
            progressPercent: 0,
          };
        }),
      },
    };
    
    setCurrentBudgetData(updatedBudgetData);
    setIsDirty(false);
    setCostBudgetChanges(null);
    setHasUserInteracted(false);
    
    // Reset undo/redo history when changing years
    setHistory([]);
    setHistoryIndex(-1);
    
    console.log(`[EditCostBudgetPage] Year changed to: ${year}`);
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setError(null);

      const updatedBudget: Partial<BudgetData> = {};
      
      if (costBudgetChanges) {
        updatedBudget.costBudget = costBudgetChanges;
      }

      await onSave(updatedBudget);
      
      // Reset state
      setIsDirty(false);
      setCostBudgetChanges(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save budget');
    } finally {
      setIsSaving(false);
    }
  };

  const handleBack = () => {
    if (isDirty) {
      setShowUnsavedChangesDialog(true);
    } else {
      onCancel();
    }
  };

  const handleCancel = () => {
    onCancel();
  };

  const handleConfirmLeave = () => {
    setShowUnsavedChangesDialog(false);
    onCancel();
  };

  // Undo handler
  const handleUndo = useCallback(() => {
    if (historyIndex > 0) {
      isUndoRedoInProgress.current = true;
      
      const newIndex = historyIndex - 1;
      const previousState = history[newIndex];
      setHistoryIndex(newIndex);
      setCostBudgetChanges(previousState);
      setCurrentBudgetData(prev => ({
        ...prev,
        costBudget: previousState,
      }));
      setIsDirty(newIndex > 0);
      setUndoRedoVersion(v => v + 1);
      
      setTimeout(() => {
        isUndoRedoInProgress.current = false;
      }, 100);
    }
  }, [historyIndex, history]);

  // Redo handler
  const handleRedo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      isUndoRedoInProgress.current = true;
      
      const newIndex = historyIndex + 1;
      const nextState = history[newIndex];
      setHistoryIndex(newIndex);
      setCostBudgetChanges(nextState);
      setCurrentBudgetData(prev => ({
        ...prev,
        costBudget: nextState,
      }));
      setIsDirty(true);
      setUndoRedoVersion(v => v + 1);
      
      setTimeout(() => {
        isUndoRedoInProgress.current = false;
      }, 100);
    }
  }, [historyIndex, history]);

  // Keyboard shortcuts for undo/redo
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      } else if ((e.ctrlKey || e.metaKey) && (e.shiftKey && e.key === 'z' || e.key === 'y')) {
        e.preventDefault();
        handleRedo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleUndo, handleRedo]);

  // Fetch default GlobalCategories to auto-populate when no cost budget categories exist
  const [defaultGlobalCategories, setDefaultGlobalCategories] = useState<CostCategory[]>([]);
  
  useEffect(() => {
    if (currentBudgetData.costBudget.categories.length === 0) {
      fetchDefaultCategoriesAsCostCategories()
        .then(categories => {
          console.log('[EditCostBudgetPage] Loaded default GlobalCategories:', categories.length);
          setDefaultGlobalCategories(categories);
        })
        .catch(err => {
          console.warn('[EditCostBudgetPage] Failed to load default GlobalCategories:', err);
        });
    }
  }, [currentBudgetData.costBudget.categories.length]);
  
  const availableCategories = currentBudgetData.costBudget.categories.length > 0
    ? currentBudgetData.costBudget.categories
    : defaultGlobalCategories;

  return (
    <div className="min-h-screen bg-background flex flex-col rounded-lg">
      {/* Header */}
      <div className="bg-background border-b">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleBack}
                disabled={isSaving}
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold">Edit Cost Budget</h1>
                <p className="text-sm text-muted-foreground mt-1">
                  Update budget values for {currentBudgetData.projectName}
                </p>
              </div>
              
              {/* Compact Unsaved Changes Indicator */}
              {isDirty && (
                <div className="flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 border border-amber-200 rounded-md text-amber-700 text-sm">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  <span className="whitespace-nowrap">Unsaved changes</span>
                </div>
              )}
            </div>
            
            {/* Import and Export Buttons */}
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => toast.info('Export feature coming soon')}
                disabled={isSaving}
                title="Export budget"
              >
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => toast.info('Import feature coming soon')}
                disabled={isSaving}
              >
                <Upload className="h-4 w-4 mr-2" />
                Import
              </Button>
            </div>
          </div>

          {/* Error Alert */}
          {error && (
            <div className="mt-3">
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            </div>
          )}
        </div>
      </div>

      {/* Main Content - Scrollable */}
      <div className="flex-1 overflow-y-auto">
        <div className="px-4 py-4">
          <EditCostBudgetForm
            key={`${selectedYear}-${undoRedoVersion}`}
            costBudget={currentBudgetData.costBudget}
            selectedYear={selectedYear}
            projectStartDate={projectStartDate}
            projectEndDate={projectEndDate}
            availableCategories={availableCategories}
            resourceBudget={currentBudgetData.resourceBudget}
            onChange={(changes) => {
              setCostBudgetChanges(changes);
              
              // Skip history updates during undo/redo operations
              if (isUndoRedoInProgress.current) {
                return;
              }
              
              // First onChange call - initialize history
              if (!hasUserInteracted) {
                setHasUserInteracted(true);
                setHistory([currentBudgetData.costBudget]);
                setHistoryIndex(0);
                return;
              }
              
              // User has interacted - this is a real change
              setIsDirty(true);
              
              // Push to history for undo/redo
              const newHistory = history.slice(0, historyIndex + 1);
              newHistory.push(changes);
              
              // Limit history to MAX_HISTORY items
              if (newHistory.length > MAX_HISTORY) {
                newHistory.shift();
                setHistory(newHistory);
                setHistoryIndex(newHistory.length - 1);
              } else {
                setHistory(newHistory);
                setHistoryIndex(newHistory.length - 1);
              }
            }}
          />
        </div>
      </div>

      {/* Fixed Footer */}
      <div className="sticky bottom-0 z-10 bg-background border-t shadow-lg">
        <div className="px-4 py-3">
          <div className="flex justify-end items-center gap-3">
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={isSaving}
              size="lg"
            >
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
            
            <Button
              variant="outline"
              onClick={handleUndo}
              disabled={historyIndex <= 0 || isSaving}
              size="lg"
              title="Undo (Ctrl+Z)"
            >
              <Undo className="h-4 w-4 mr-2" />
              Undo {historyIndex > 0 && `(${historyIndex})`}
            </Button>
            
            <Button
              variant="outline"
              onClick={handleRedo}
              disabled={historyIndex >= history.length - 1 || isSaving}
              size="lg"
              title="Redo (Ctrl+Shift+Z)"
            >
              <Redo className="h-4 w-4 mr-2" />
              Redo {historyIndex < history.length - 1 && `(${history.length - 1 - historyIndex})`}
            </Button>
            
            <Button
              onClick={handleSave}
              disabled={!isDirty || isSaving}
              size="lg"
            >
              <Save className="h-4 w-4 mr-2" />
              {isSaving ? 'Saving...' : 'Save Budget'}
            </Button>
          </div>
        </div>
      </div>

      {/* Unsaved Changes Dialog */}
      <AlertDialog open={showUnsavedChangesDialog} onOpenChange={setShowUnsavedChangesDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure that you want to leave the current page? The changes that you made won't be saved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmLeave}>Leave</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
